import { useCallback, useEffect, useRef, useState } from "react";
import type { UseFormGetValues, UseFormSetValue } from "react-hook-form";
import { generateSlopify, SlopifyClientError } from "../../util/slopifyClient";
import { propagateSlopify, SlopifyPropagateError } from "../../util/slopifyPropagateClient";
import { buildProposal } from "../../util/proposalDiff";
import type {
  ThreadState,
  Turn,
  TurnErrorKind,
  TurnLanguageProposal,
} from "../../util/slopifyThread";
import {
  clearThread,
  getThread,
  isGenerationProposal,
  isPropagationProposal,
  newTurnId,
  setThread,
} from "../../util/slopifyThread";
import { getExcludedLayers } from "../../util/slopifyContextPrefs";
import { useScopeContext } from "./ScopeContext";

export interface UseAssistantScenarIaRailOptions {
  contentId: number | undefined;
  activeLanguageId: number | undefined;
  sourceLanguageId: number | undefined;
  apiUrl: string;
  propagateApiUrl: string;
  setValue: UseFormSetValue<any>;
  getValues: UseFormGetValues<any>;
  setHasGeneratedContent: (value: boolean) => void;
  onGeneratingChange: (value: boolean) => void;
  onFieldsApplied?: (fieldNames: string[]) => void;
  dirtyFieldNames: string[];
  onApplyLanguageTranslations?: (
    languageId: number,
    translations: Record<string, unknown>,
  ) => Promise<void> | void;
}

export interface PropagationStartParams {
  sourceLanguageId: number;
  targetLanguageIds: number[];
  fieldNames: string[];
  fieldValues?: Record<string, unknown>;
  promptLabel?: string;
}

export interface UseAssistantScenarIaRailResult {
  prompt: string;
  setPrompt: (next: string) => void;
  turns: Turn[];
  inFlightTurn: Turn | undefined;
  liveTurn: Turn | undefined;
  historyTurns: Turn[];
  failedTurns: Turn[];
  manuallyDirtied: Set<string>;
  rateLimitRemaining: number | undefined;
  rateLimitResetAt: number | undefined;
  contextLayerCount: number;
  isGenerating: boolean;
  submit: (promptOverride?: string) => void;
  abort: () => void;
  retry: (turn: Turn) => void;
  acceptTurn: (turnId: string, fieldNames: string[]) => void;
  rejectTurn: (turnId: string) => void;
  refineTurn: (turn: Turn) => void;
  restoreTurn: (turnId: string) => void;
  resetConversation: () => void;
  startPropagation: (params: PropagationStartParams) => void;
  applyLanguage: (turnId: string, languageId: number) => Promise<void>;
  rejectLanguage: (turnId: string, languageId: number) => void;
  applyAllLanguages: (turnId: string) => Promise<void>;
}

export const useAssistantScenarIaRail = (
  options: UseAssistantScenarIaRailOptions,
): UseAssistantScenarIaRailResult => {
  const {
    contentId,
    activeLanguageId,
    sourceLanguageId,
    apiUrl,
    propagateApiUrl,
    setValue,
    getValues,
    setHasGeneratedContent,
    onGeneratingChange,
    onFieldsApplied,
    dirtyFieldNames,
    onApplyLanguageTranslations,
  } = options;

  const [prompt, setPrompt] = useState<string>("");
  const [thread, setThreadState] = useState<ThreadState>(() => ({ version: 1, turns: [] }));
  const [rateLimitRemaining, setRateLimitRemaining] = useState<number | undefined>(undefined);
  const [rateLimitResetAt, setRateLimitResetAt] = useState<number | undefined>(undefined);
  const [contextLayerCount, setContextLayerCount] = useState<number>(0);
  const [manuallyDirtied, setManuallyDirtied] = useState<Set<string>>(() => new Set());

  const { scopeFields } = useScopeContext();
  const scopeFieldsRef = useRef<string[]>(scopeFields);
  scopeFieldsRef.current = scopeFields;

  const abortRef = useRef<AbortController | null>(null);
  const onGeneratingChangeRef = useRef(onGeneratingChange);
  onGeneratingChangeRef.current = onGeneratingChange;
  const onFieldsAppliedRef = useRef(onFieldsApplied);
  onFieldsAppliedRef.current = onFieldsApplied;
  const onApplyLanguageTranslationsRef = useRef(onApplyLanguageTranslations);
  onApplyLanguageTranslationsRef.current = onApplyLanguageTranslations;
  const threadRef = useRef<ThreadState>(thread);
  threadRef.current = thread;
  const justAppliedFieldsRef = useRef<Set<string>>(new Set());
  const prevDirtySignatureRef = useRef<string>("");

  // Load thread whenever content/language changes.
  useEffect(() => {
    if (typeof contentId !== "number" || typeof activeLanguageId !== "number") {
      const empty: ThreadState = { version: 1, turns: [] };
      threadRef.current = empty;
      setThreadState(empty);
      setManuallyDirtied(new Set());
      return;
    }
    const loaded = getThread(contentId, activeLanguageId);
    // Any "in-flight" or "live" turn persisted from a previous session is
    // stale. Propagation turns are also dropped entirely while the
    // /api/slopify/propagate endpoint is disabled — otherwise stored
    // failures would keep rendering "Échec réseau" cards with a Réessayer
    // button that 400s on every click.
    const sanitized: ThreadState = {
      version: 1,
      turns: loaded.turns
        .filter((t) => !isPropagationProposal(t.proposal))
        .map((t) => {
          if (t.status === "in-flight") {
            return { ...t, status: "aborted", error: { kind: "aborted" } };
          }
          if (t.status === "live") {
            return { ...t, status: "rejected" };
          }
          return t;
        }),
    };
    threadRef.current = sanitized;
    setThreadState(sanitized);
    setManuallyDirtied(new Set());
    if (JSON.stringify(sanitized) !== JSON.stringify(loaded)) {
      setThread(contentId, activeLanguageId, sanitized);
    }
  }, [contentId, activeLanguageId]);

  // Track fields the user dirtied manually (not via a proposal apply).
  useEffect(() => {
    const signature = dirtyFieldNames.slice().sort().join("|");
    if (signature === prevDirtySignatureRef.current) return;
    prevDirtySignatureRef.current = signature;
    setManuallyDirtied((prev) => {
      const next = new Set(prev);
      let changed = false;
      for (const name of dirtyFieldNames) {
        if (justAppliedFieldsRef.current.has(name)) continue;
        if (!next.has(name)) {
          next.add(name);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [dirtyFieldNames]);

  const persistThread = useCallback(
    (next: ThreadState) => {
      threadRef.current = next;
      setThreadState(next);
      if (typeof contentId === "number" && typeof activeLanguageId === "number") {
        setThread(contentId, activeLanguageId, next);
      }
    },
    [contentId, activeLanguageId],
  );

  const updateTurn = useCallback(
    (turnId: string, patch: Partial<Turn>) => {
      const current = threadRef.current;
      const nextTurns = current.turns.map((t) => (t.id === turnId ? { ...t, ...patch } : t));
      persistThread({ version: 1, turns: nextTurns });
    },
    [persistThread],
  );

  const inFlightTurn = thread.turns.find((t) => t.status === "in-flight");
  const liveTurn = thread.turns.find((t) => t.status === "live");
  const historyTurns = thread.turns.filter(
    (t) =>
      t.status === "accepted" ||
      t.status === "rejected" ||
      t.status === "aborted" ||
      t.status === "failed",
  );
  const isGenerating = Boolean(inFlightTurn);

  useEffect(() => {
    onGeneratingChangeRef.current(isGenerating);
  }, [isGenerating]);

  const failedTurns = thread.turns.filter((t) => t.status === "failed" || t.status === "aborted");

  const runGeneration = useCallback(
    async (turn: Turn) => {
      if (typeof contentId !== "number") return;

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const excludeContextLayers = getExcludedLayers(contentId);
        const isTargetLanguage =
          typeof sourceLanguageId === "number" &&
          typeof activeLanguageId === "number" &&
          sourceLanguageId !== activeLanguageId;
        // Live (possibly unsaved) form state lets the AI generate against what
        // the user currently sees — e.g. answer/question rows added in the
        // editor but not yet saved. Only for source-language generation, where
        // structural add/remove happens (the backend generates in the project's
        // source language regardless of the active one).
        const currentData = isTargetLanguage
          ? undefined
          : ((getValues() ?? {}) as Record<string, unknown>);
        const result = await generateSlopify({
          contentId,
          humanPrompt: turn.prompt,
          targetLanguageId: isTargetLanguage ? activeLanguageId : undefined,
          scopeFields: turn.scope.length > 0 ? turn.scope : undefined,
          excludeContextLayers: excludeContextLayers.length > 0 ? excludeContextLayers : undefined,
          currentData,
          signal: controller.signal,
          apiUrl,
        });

        if (typeof result.rateLimitRemaining === "number") {
          setRateLimitRemaining(result.rateLimitRemaining);
        }
        if (typeof result.rateLimitReset === "number") {
          setRateLimitResetAt(result.rateLimitReset);
        }
        if (Array.isArray(result.contextSummary)) {
          setContextLayerCount(result.contextSummary.length);
        }

        const currentFormValues = (getValues() ?? {}) as Record<string, unknown>;
        const proposal = buildProposal({
          generatedData: result.generatedData,
          currentFormValues,
        });

        updateTurn(turn.id, {
          status: "live",
          proposal: {
            kind: "generation",
            fields: proposal.fields,
            contextSummary: result.contextSummary ?? null,
          },
        });
      } catch (err) {
        const kind: TurnErrorKind = err instanceof SlopifyClientError ? err.kind : "unknown";
        const message =
          err instanceof SlopifyClientError ? err.message : String((err as Error)?.message ?? err);
        const retryAfter = err instanceof SlopifyClientError ? err.retryAfter : undefined;

        updateTurn(turn.id, {
          status: kind === "aborted" ? "aborted" : "failed",
          error: { kind, message, retryAfter },
        });
      } finally {
        if (abortRef.current === controller) abortRef.current = null;
      }
    },
    [contentId, activeLanguageId, sourceLanguageId, apiUrl, getValues, updateTurn],
  );

  const submit = useCallback(
    (promptOverride?: string) => {
      if (typeof contentId !== "number" || typeof activeLanguageId !== "number") return;
      if (abortRef.current) return; // block re-entrancy while in-flight

      const effectivePrompt = typeof promptOverride === "string" ? promptOverride : prompt;

      // Supersede any existing live proposal — mark it rejected silently.
      const supersededTurns = threadRef.current.turns.map((t) =>
        t.status === "live" ? { ...t, status: "rejected" as const } : t,
      );

      const turn: Turn = {
        id: newTurnId(),
        createdAt: Date.now(),
        prompt: effectivePrompt,
        scope: scopeFieldsRef.current.slice(),
        status: "in-flight",
      };
      const next: ThreadState = {
        version: 1,
        turns: [...supersededTurns, turn],
      };
      persistThread(next);
      setPrompt("");
      void runGeneration(turn);
    },
    [contentId, activeLanguageId, prompt, persistThread, runGeneration],
  );

  const abort = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
    }
  }, []);

  const retry = useCallback(
    (failedTurn: Turn) => {
      if (typeof contentId !== "number" || typeof activeLanguageId !== "number") return;
      if (abortRef.current) return;
      // Reuse the failed turn in place: flip back to in-flight, clear error, refresh timestamp.
      // The failed card transforms into an in-flight card at the same DOM slot — no stacking.
      const refreshed: Turn = {
        ...failedTurn,
        status: "in-flight",
        error: undefined,
        createdAt: Date.now(),
      };
      updateTurn(failedTurn.id, {
        status: "in-flight",
        error: undefined,
        createdAt: Date.now(),
      });
      void runGeneration(refreshed);
    },
    [contentId, activeLanguageId, updateTurn, runGeneration],
  );

  const acceptTurn = useCallback(
    (turnId: string, fieldNames: string[]) => {
      const current = threadRef.current;
      const turn = current.turns.find((t) => t.id === turnId);
      if (!turn || !isGenerationProposal(turn.proposal)) return;

      const applied: string[] = [];
      for (const field of turn.proposal.fields) {
        if (!fieldNames.includes(field.fieldName)) continue;
        if (field.unchanged) continue;
        justAppliedFieldsRef.current.add(field.fieldName);
        setValue(field.fieldName, field.after as any, {
          shouldDirty: true,
          shouldTouch: true,
        });
        applied.push(field.fieldName);
      }

      updateTurn(turnId, {
        status: "accepted",
        appliedCount: applied.length,
        acceptedFieldNames: applied.slice(),
      });
      if (applied.length > 0) {
        setHasGeneratedContent(true);
        onFieldsAppliedRef.current?.(applied);
      }

      // Reset manuallyDirtied tracking — any future edits count as fresh manual edits.
      setManuallyDirtied(new Set());

      // After react-hook-form processes the setValue calls, drop them from the
      // justApplied set so real user edits on these fields count as manual again.
      window.setTimeout(() => {
        for (const name of applied) {
          justAppliedFieldsRef.current.delete(name);
        }
      }, 500);
    },
    [setValue, setHasGeneratedContent, updateTurn],
  );

  const rejectTurn = useCallback(
    (turnId: string) => {
      updateTurn(turnId, { status: "rejected" });
    },
    [updateTurn],
  );

  const refineTurn = useCallback((turn: Turn) => {
    setPrompt(turn.prompt);
  }, []);

  const restoreTurn = useCallback(
    (turnId: string) => {
      const current = threadRef.current;
      const turn = current.turns.find((t) => t.id === turnId);
      if (!turn || !isGenerationProposal(turn.proposal)) return;

      const applied: string[] = [];
      for (const field of turn.proposal.fields) {
        justAppliedFieldsRef.current.add(field.fieldName);
        setValue(field.fieldName, field.after as any, {
          shouldDirty: true,
          shouldTouch: true,
        });
        applied.push(field.fieldName);
      }

      if (applied.length > 0) {
        setHasGeneratedContent(true);
        onFieldsAppliedRef.current?.(applied);
      }
      setManuallyDirtied(new Set());

      window.setTimeout(() => {
        for (const name of applied) {
          justAppliedFieldsRef.current.delete(name);
        }
      }, 500);
    },
    [setValue, setHasGeneratedContent],
  );

  const resetConversation = useCallback(() => {
    if (typeof contentId !== "number" || typeof activeLanguageId !== "number") return;
    if (abortRef.current) {
      abortRef.current.abort();
    }
    clearThread(contentId, activeLanguageId);
    const empty: ThreadState = { version: 1, turns: [] };
    threadRef.current = empty;
    setThreadState(empty);
    setManuallyDirtied(new Set());
    setPrompt("");
  }, [contentId, activeLanguageId]);

  const runPropagation = useCallback(
    async (
      turn: Turn,
      params: {
        sourceLanguageId: number;
        targetLanguageIds: number[];
        fieldNames: string[];
        fieldValues?: Record<string, unknown>;
      },
    ) => {
      if (typeof contentId !== "number") return;

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const result = await propagateSlopify({
          contentId,
          sourceLanguageId: params.sourceLanguageId,
          targetLanguageIds: params.targetLanguageIds,
          fieldNames: params.fieldNames,
          fieldValues: params.fieldValues,
          signal: controller.signal,
          apiUrl: propagateApiUrl,
        });

        if (typeof result.rateLimitRemaining === "number") {
          setRateLimitRemaining(result.rateLimitRemaining);
        }
        if (typeof result.rateLimitReset === "number") {
          setRateLimitResetAt(result.rateLimitReset);
        }

        const failureById = new Map<number, string>();
        for (const f of result.failures) {
          failureById.set(Number(f.languageId), f.error);
        }

        const languages: TurnLanguageProposal[] = params.targetLanguageIds.map((languageId) => {
          const hit = result.results.find((r) => Number(r.languageId) === Number(languageId));
          if (hit) {
            return {
              languageId,
              translations: hit.translations,
              status: "pending" as const,
            };
          }
          return {
            languageId,
            translations: {},
            status: "failed" as const,
            failureError: failureById.get(languageId) || "Translation failed",
          };
        });

        updateTurn(turn.id, {
          status: "live",
          proposal: {
            kind: "propagation",
            sourceLanguageId: params.sourceLanguageId,
            fieldNames: params.fieldNames,
            languages,
          },
        });
      } catch (err) {
        const kind: TurnErrorKind = err instanceof SlopifyPropagateError ? err.kind : "unknown";
        const message =
          err instanceof SlopifyPropagateError
            ? err.message
            : String((err as Error)?.message ?? err);
        const retryAfter = err instanceof SlopifyPropagateError ? err.retryAfter : undefined;

        updateTurn(turn.id, {
          status: kind === "aborted" ? "aborted" : "failed",
          error: { kind, message, retryAfter },
        });
      } finally {
        if (abortRef.current === controller) abortRef.current = null;
      }
    },
    [contentId, propagateApiUrl, updateTurn],
  );

  const startPropagation = useCallback(
    (params: PropagationStartParams) => {
      if (typeof contentId !== "number" || typeof activeLanguageId !== "number") return;
      if (abortRef.current) return;
      if (params.targetLanguageIds.length === 0 || params.fieldNames.length === 0) return;

      const supersededTurns = threadRef.current.turns.map((t) =>
        t.status === "live" ? { ...t, status: "rejected" as const } : t,
      );

      const turn: Turn = {
        id: newTurnId(),
        createdAt: Date.now(),
        prompt: params.promptLabel ?? "[Propagation]",
        scope: params.fieldNames.slice(),
        status: "in-flight",
      };
      persistThread({ version: 1, turns: [...supersededTurns, turn] });
      void runPropagation(turn, {
        sourceLanguageId: params.sourceLanguageId,
        targetLanguageIds: params.targetLanguageIds,
        fieldNames: params.fieldNames,
        fieldValues: params.fieldValues,
      });
    },
    [contentId, activeLanguageId, persistThread, runPropagation],
  );

  const updatePropagationLanguage = useCallback(
    (turnId: string, languageId: number, patch: Partial<TurnLanguageProposal>) => {
      const current = threadRef.current;
      const nextTurns = current.turns.map((t) => {
        if (t.id !== turnId || !isPropagationProposal(t.proposal)) return t;
        const languages = t.proposal.languages.map((lang) =>
          Number(lang.languageId) === Number(languageId) ? { ...lang, ...patch } : lang,
        );
        const allTerminal = languages.every((l) => l.status !== "pending");
        const appliedCount = languages.filter((l) => l.status === "applied").length;
        return {
          ...t,
          proposal: { ...t.proposal, languages },
          status: allTerminal ? ("accepted" as const) : t.status,
          appliedCount: allTerminal ? appliedCount : t.appliedCount,
        };
      });
      persistThread({ version: 1, turns: nextTurns });
    },
    [persistThread],
  );

  const applyLanguage = useCallback(
    async (turnId: string, languageId: number) => {
      const current = threadRef.current;
      const turn = current.turns.find((t) => t.id === turnId);
      if (!turn || !isPropagationProposal(turn.proposal)) return;
      const lang = turn.proposal.languages.find((l) => Number(l.languageId) === Number(languageId));
      if (!lang || lang.status !== "pending") return;

      const handler = onApplyLanguageTranslationsRef.current;
      if (!handler) {
        updatePropagationLanguage(turnId, languageId, {
          status: "failed",
          failureError: "No apply handler configured",
        });
        return;
      }

      try {
        await handler(languageId, lang.translations);
        updatePropagationLanguage(turnId, languageId, { status: "applied" });
        setHasGeneratedContent(true);
      } catch (err: any) {
        updatePropagationLanguage(turnId, languageId, {
          status: "failed",
          failureError: err?.message || "Apply failed",
        });
      }
    },
    [updatePropagationLanguage, setHasGeneratedContent],
  );

  const rejectLanguage = useCallback(
    (turnId: string, languageId: number) => {
      updatePropagationLanguage(turnId, languageId, { status: "rejected" });
    },
    [updatePropagationLanguage],
  );

  const applyAllLanguages = useCallback(
    async (turnId: string) => {
      const current = threadRef.current;
      const turn = current.turns.find((t) => t.id === turnId);
      if (!turn || !isPropagationProposal(turn.proposal)) return;
      const pendingIds = turn.proposal.languages
        .filter((l) => l.status === "pending")
        .map((l) => l.languageId);
      for (const id of pendingIds) {
        // Sequential to avoid hammering the PLD endpoint and to keep thread
        // state transitions predictable.

        await applyLanguage(turnId, id);
      }
    },
    [applyLanguage],
  );

  return {
    prompt,
    setPrompt,
    turns: thread.turns,
    inFlightTurn,
    liveTurn,
    historyTurns,
    failedTurns,
    manuallyDirtied,
    rateLimitRemaining,
    rateLimitResetAt,
    contextLayerCount,
    isGenerating,
    submit,
    abort,
    retry,
    acceptTurn,
    rejectTurn,
    refineTurn,
    restoreTurn,
    resetConversation,
    startPropagation,
    applyLanguage,
    rejectLanguage,
    applyAllLanguages,
  };
};
